import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconCameraFlip, IconClose } from "./icons";

type Facing = "user" | "environment";
type Orientation = "landscape" | "portrait";

type Props = {
  open: boolean;
  onClose: () => void;
  onCapture: (dataUrl: string) => void;
  title?: string;
  hint?: string;
  guide?: ReactNode;
  orientation?: Orientation;
  width?: number;
  height?: number;
  flash?: boolean;
  miroirHorizontal?: boolean;
  miroirVertical?: boolean;
};

export function CameraSheet({
  open,
  onClose,
  onCapture,
  title = "Photo d'urgence",
  hint,
  guide,
  orientation,
  width,
  height,
  flash = false,
  miroirHorizontal = false,
  miroirVertical = false,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [facing, setFacing] = useState<Facing>("environment");

  useEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }
    let cancelled = false;
    setError(null);
    setReady(false);

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      setError("Caméra non supportée sur ce navigateur.");
      return;
    }

    const constraints: MediaStreamConstraints = {
      video: {
        facingMode: { ideal: facing },
        ...(width ? { width: { ideal: width } } : {}),
        ...(height ? { height: { ideal: height } } : {}),
      },
      audio: false,
    };

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video
            .play()
            .then(() => setReady(true))
            .catch(() => setReady(true));
        }
        // Tente d'activer/désactiver le flash (torch) selon la planche.
        // Non supporté sur la plupart des caméras avant et sur iOS Safari : on ignore l'échec.
        if (facing === "environment") {
          const [track] = stream.getVideoTracks();
          if (track) {
            track
              .applyConstraints({
                advanced: [{ torch: flash } as MediaTrackConstraintSet],
              })
              .catch(() => {});
          }
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg =
          e instanceof Error
            ? e.name === "NotAllowedError"
              ? "Accès à la caméra refusé."
              : e.message
            : "Impossible d'accéder à la caméra.";
        setError(msg);
      });

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [open, facing, flash, width, height]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const flipX = miroirHorizontal;
    const flipY = miroirVertical;
    if (flipX || flipY) {
      ctx.translate(flipX ? canvas.width : 0, flipY ? canvas.height : 0);
      ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
    }
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    onCapture(dataUrl);
  };

  if (!open || typeof document === "undefined") return null;

  // Aperçu miroir uniquement en selfie (convention standard des apps caméra)
  const previewMirror = facing === "user" ? "scale-x-[-1]" : "";
  // Quand le shot doit être pris en paysage, on fait pivoter le guide et le hint
  // de 90° pour signaler à l'utilisateur qu'il doit tourner son téléphone.
  const landscapeHint = orientation === "landscape";

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-black">
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className={`absolute inset-0 size-full object-cover ${previewMirror}`}
      />

      {ready && !error && guide ? (
        <div
          className={`pointer-events-none absolute inset-0 ${landscapeHint ? "rotate-90" : ""}`}
        >
          {guide}
        </div>
      ) : null}

      <header className="relative z-10 flex items-center justify-between px-4 pb-3 pt-[max(env(safe-area-inset-top),0.75rem)]">
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer la caméra"
          className="inline-flex size-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur transition active:scale-95"
        >
          <IconClose width={22} height={22} strokeWidth={2.5} />
        </button>
        <span className="rounded-full bg-black/40 px-3 py-1 text-[11.5px] font-semibold uppercase tracking-wider text-white backdrop-blur">
          {title}
        </span>
        <span className="size-11" aria-hidden />
      </header>

      {hint ? (
        <div
          className={`relative z-10 mx-auto mt-1 max-w-xs rounded-2xl bg-black/55 px-3 py-2 text-center text-[12.5px] font-medium text-white backdrop-blur ${landscapeHint ? "rotate-90" : ""}`}
        >
          {hint}
        </div>
      ) : null}

      <div className="relative z-10 mt-auto flex flex-col items-center gap-4 px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)]">
        {error ? (
          <div className="max-w-xs rounded-2xl bg-black/70 px-4 py-3 text-center text-[13px] font-medium text-white">
            {error}
          </div>
        ) : !ready ? (
          <div className="rounded-2xl bg-black/40 px-4 py-2 text-[12px] text-white backdrop-blur">
            Activation de la caméra…
          </div>
        ) : null}

        <div className="flex w-full items-center justify-center gap-8">
          <span className="size-14" aria-hidden />
          <button
            type="button"
            onClick={capture}
            disabled={!!error || !ready}
            aria-label="Prendre la photo"
            className="inline-flex size-20 items-center justify-center rounded-full bg-white ring-4 ring-white/30 transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <span className="block size-16 rounded-full border-[3px] border-black/10" />
          </button>
          <button
            type="button"
            onClick={() =>
              setFacing((f) => (f === "user" ? "environment" : "user"))
            }
            disabled={!!error}
            aria-label="Inverser la caméra"
            aria-pressed={facing === "user"}
            className="inline-flex size-14 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur transition active:scale-95 disabled:opacity-40"
          >
            <IconCameraFlip width={24} height={24} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
